<template>
  <div>
    <v-container grid-list-md>
      <v-layout row wrap>
        <v-flex xs12>
          <v-card class="elevation-3">
            <v-toolbar dark color="primary" class="mb-3">
              <v-toolbar-title>{{ $t('security_create_company') }}</v-toolbar-title>
            </v-toolbar>

            <v-card-text>
              <v-progress-linear v-if="loading" indeterminate class="mb-3"></v-progress-linear>

              <v-alert v-if="error" type="error" dismissible class="mb-3" @dismissed="error = null">
                {{ error }}
              </v-alert>

              <!-- Indicador de Paso -->
              <div class="mb-4">
                <v-stepper v-model="currentStep" non-linear>
                  <v-stepper-header>
                    <v-stepper-step :complete="currentStep > 1" step="1">{{ $t('step') }} 1</v-stepper-step>
                    <v-divider></v-divider>
                    <v-stepper-step :complete="currentStep > 2" step="2">{{ $t('step') }} 2</v-stepper-step>
                    <v-divider></v-divider>
                    <v-stepper-step :complete="currentStep > 3" step="3">{{ $t('step') }} 3</v-stepper-step>
                    <v-divider></v-divider>
                    <v-stepper-step step="4">{{ $t('step') }} 4</v-stepper-step>
                  </v-stepper-header>
                </v-stepper>
              </div>

              <!-- Formulario con Pasos -->
              <v-form @submit.prevent="submitCreateCompany" ref="createCompanyForm">
                <v-window v-model="currentStep">
                  
                  <!-- PASO 1: Datos Personales del Admin -->
                  <v-window-item :value="1">
                    <v-card class="mb-4 pa-4">
                      <v-card-title class="mb-3">{{ $t('security_credentials') }}</v-card-title>

                      <!-- Datos Pre-rellenados (Read-only) -->
                      <v-layout row wrap class="mb-4 pa-3 grey lighten-4" style="border-radius: 4px;">
                        <v-flex xs12 sm6>
                          <p><strong>{{ $t('email') }}:</strong></p>
                          <p>{{ invitationData ? invitationData.email : 'Cargando...' }}</p>
                        </v-flex>
                        <v-flex xs12 sm6>
                          <p><strong>{{ $t('first_name') }}:</strong></p>
                          <p>{{ invitationData ? invitationData.first_name : 'Cargando...' }}</p>
                        </v-flex>
                        <v-flex xs12 sm6>
                          <p><strong>{{ $t('last_name') }}:</strong></p>
                          <p>{{ invitationData ? invitationData.last_name : 'Cargando...' }}</p>
                        </v-flex>
                      </v-layout>

                      <!-- Contraseña -->
                      <v-text-field
                        v-model="form.password"
                        :label="$t('password')"
                        type="password"
                        required
                        :error-messages="errors.password"
                        @blur="validatePassword"
                        class="mb-3"
                        outlined
                        hint="Mínimo 8 caracteres"
                      ></v-text-field>

                      <!-- Teléfono -->
                      <label class="mb-2">{{ $t('telephone') }}</label>
                      <vue-phone-number-input
                        default-country-code="MX"
                        color="#D32F2F"
                        valid-color="#2196F3"
                        @update="onPhoneNumberUpdate"
                        v-model="form.telephone"
                        :translations="phoneTranslations"
                      ></vue-phone-number-input>
                      <p v-if="errors.telephone" style="color: #d32f2f; font-size: 0.75rem;">{{ errors.telephone[0] }}</p>
                    </v-card>
                  </v-window-item>

                  <!-- PASO 2: Información de Empresa -->
                  <v-window-item :value="2">
                    <v-card class="mb-4 pa-4">
                      <v-card-title class="mb-3">{{ $t('security_company_info') }}</v-card-title>

                      <v-text-field
                        v-model="form.name"
                        :label="$t('company_name')"
                        required
                        :error-messages="errors.name"
                        @blur="validateName"
                        class="mb-3"
                        outlined
                      ></v-text-field>

                      <v-text-field
                        v-model="form.address"
                        :label="$t('address')"
                        required
                        :error-messages="errors.address"
                        @blur="validateAddress"
                        class="mb-3"
                        outlined
                      ></v-text-field>

                      <v-text-field
                        v-model="form.company_email"
                        :label="$t('email')"
                        type="email"
                        required
                        :error-messages="errors.company_email"
                        @blur="validateCompanyEmail"
                        class="mb-3"
                        outlined
                      ></v-text-field>

                      <v-text-field
                        v-model="form.web_page"
                        :label="$t('web_page')"
                        class="mb-3"
                        outlined
                      ></v-text-field>


                    </v-card>
                  </v-window-item>

                  <!-- PASO 3: Ubicación -->
                  <v-window-item :value="3">
                    <v-card class="mb-4 pa-4">
                      <v-card-title class="mb-3">{{ $t('security_location') }}</v-card-title>

                      <v-layout row wrap>
                        <v-flex xs12 sm6 class="pr-2">
                          <v-text-field
                            v-model.number="form.latitude"
                            :label="$t('latitude')"
                            type="number"
                            step="0.000001"
                            class="mb-3"
                            outlined
                            hint="Opcional"
                            persistent-hint
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6 class="pl-2">
                          <v-text-field
                            v-model.number="form.longitude"
                            :label="$t('longitude')"
                            type="number"
                            step="0.000001"
                            class="mb-3"
                            outlined
                            hint="Opcional"
                            persistent-hint
                          ></v-text-field>
                        </v-flex>
                      </v-layout>

                      <v-text-field
                        v-model="form.url_map"
                        :label="$t('map_url')"
                        class="mb-3"
                        outlined
                      ></v-text-field>
                    </v-card>
                  </v-window-item>

                  <!-- PASO 4: Documentos -->
                  <v-window-item :value="4">
                    <v-card class="mb-4 pa-4">
                      <v-card-title class="mb-3">{{ $t('security_documents') }}</v-card-title>

                      <v-layout row wrap class="text-xs-center">
                        <v-flex xs12 class="ingla pt-3 pb-3 pl-3 pr-3">
                          <v-label>{{ $t('company_logo') }}</v-label>
                          <br>
                          <v-img
                            v-if="logoPreview"
                            :src="logoPreview"
                            height="200"
                            class="mb-3"
                          ></v-img>
                          <v-img
                            v-else
                            src="https://via.placeholder.com/400x200?text=Sin+Logo"
                            height="200"
                            class="mb-3"
                          ></v-img>
                          <br>
                          <v-alert
                            v-if="logoFile"
                            type="success"
                            class="mb-3"
                            transition="scale-transition"
                          >
                            {{ $t('file_selected') }}: {{ logoFile.name }}
                          </v-alert>
                          <br>
                          <v-btn
                            color="primary"
                            @click="pickFileLogo"
                            outlined
                          >
                            <v-icon left>cloud_upload</v-icon>
                            {{ $t('select_file') }}
                          </v-btn>
                          <input
                            ref="logoInput"
                            type="file"
                            @change="onLogoSelected"
                            accept="image/*"
                            style="display: none"
                          >
                          <p v-if="errors.logo" style="color: red; font-size: 0.75rem; margin-top: 10px;">
                            {{ errors.logo[0] }}
                          </p>
                        </v-flex>
                      </v-layout>
                    </v-card>
                  </v-window-item>
                </v-window>

                <!-- Botones de Navegación -->
                <v-divider class="my-4"></v-divider>
                <v-card-actions>
                  <v-btn
                    v-if="currentStep > 1"
                    color="grey"
                    @click="currentStep--"
                    outlined
                  >
                    <v-icon left>arrow_back</v-icon>
                    {{ $t('back') }}
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    v-if="currentStep < 4"
                    color="primary"
                    @click="nextStep"
                  >
                    {{ $t('continuar') }}
                    <v-icon right>arrow_forward</v-icon>
                  </v-btn>
                  <v-btn
                    v-else
                    color="primary"
                    type="submit"
                    :loading="loading"
                    :disabled="!formValid"
                  >
                    {{ $t('security_create_company') }}
                  </v-btn>
                </v-card-actions>
              </v-form>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios'
import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'

const API_BASE_URL = 'https://empresa-service.intrare.app'

export default {
  name: 'SecurityRegister',
  components: {
    VuePhoneNumberInput
  },
  data() {
    return {
      currentStep: 1,
      token: null,
      invitationData: null,
      logoFile: null,
      logoPreview: null,
      loading: false,
      error: null,
      form: {
        password: '',
        telephone: '',
        name: '',
        address: '',
        company_email: '',
        web_page: '',
        scian: '',
        classification: '',
        latitude: '',
        longitude: '',
        url_map: '',
        validity: new Date().toISOString().split('T')[0]
      },
      errors: {
        password: [],
        telephone: [],
        name: [],
        address: [],
        company_email: [],
        logo: []
      },
      emailRegex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      phoneTranslations: {
        countrySelector: {
          placeholder: 'Selecciona un país'
        },
        phoneInput: {
          placeholder: 'Número de teléfono'
        }
      }
    }
  },
  computed: {
    formValid() {
      return (
        this.form.password &&
        this.form.password.length >= 8 &&
        this.form.telephone &&
        this.form.telephone.length > 0 &&
        this.form.name &&
        this.form.name.trim().length > 0 &&
        this.form.address &&
        this.form.address.trim().length > 0 &&
        this.form.company_email &&
        this.emailRegex.test(this.form.company_email) &&
        this.logoFile
      )
    }
  },
  methods: {
    fetchInvitationData() {
      this.loading = true
      this.error = null
      const config = {
        method: 'get',
        url: `${API_BASE_URL}/security/invite/${this.token}/`,
        headers: {
          'Content-Type': 'application/json'
        }
      }
      axios(config)
        .then(resp => {
          this.invitationData = resp.data
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          this.error = (err.response && err.response.data && err.response.data.detail) || this.$t('error_fetching_invitation')
        })
    },
    onPhoneNumberUpdate(data) {
      this.form.telephone = data.formattedNumber || ''
    },
    pickFileLogo() {
      this.$refs.logoInput.click()
    },
    onLogoSelected(event) {
      const file = event.target.files[0]
      if (file) {
        if (file.size > 5 * 1024 * 1024) { // 5MB
          this.errors.logo = [this.$t('file_too_large')]
          this.logoFile = null
          this.logoPreview = null
        } else {
          this.logoFile = file
          this.errors.logo = []
          
          // Crear preview
          const reader = new FileReader()
          reader.onload = (e) => {
            this.logoPreview = e.target.result
          }
          reader.readAsDataURL(file)
        }
      }
    },
    validatePassword() {
      this.errors.password = []
      if (!this.form.password) {
        this.errors.password.push(this.$t('password_required'))
      } else if (this.form.password.length < 8) {
        this.errors.password.push(this.$t('password_min_length'))
      }
    },
    validateTelephone() {
      this.errors.telephone = []
      if (!this.form.telephone) {
        this.errors.telephone.push(this.$t('telephone_required'))
      }
    },
    validateName() {
      this.errors.name = []
      if (!this.form.name || this.form.name.trim().length === 0) {
        this.errors.name.push(this.$t('name_required'))
      }
    },
    validateAddress() {
      this.errors.address = []
      if (!this.form.address || this.form.address.trim().length === 0) {
        this.errors.address.push(this.$t('address_required'))
      }
    },
    validateCompanyEmail() {
      this.errors.company_email = []
      if (!this.form.company_email) {
        this.errors.company_email.push(this.$t('email_required'))
      } else if (!this.emailRegex.test(this.form.company_email)) {
        this.errors.company_email.push(this.$t('email_invalid'))
      }
    },
    nextStep() {
      // Validar antes de avanzar
      if (this.currentStep === 1) {
        this.validatePassword()
        this.validateTelephone()
        if (this.errors.password.length === 0 && this.errors.telephone.length === 0) {
          this.currentStep++
        }
      } else if (this.currentStep === 2) {
        this.validateName()
        this.validateAddress()
        this.validateCompanyEmail()
        if (this.errors.name.length === 0 && this.errors.address.length === 0 && this.errors.company_email.length === 0) {
          this.currentStep++
        }
      } else {
        this.currentStep++
      }
    },
    submitCreateCompany() {
      // Validar todos los campos antes de enviar
      this.validatePassword()
      this.validateTelephone()
      this.validateName()
      this.validateAddress()
      this.validateCompanyEmail()

      if (this.formValid) {
        this.loading = true
        this.error = null

        // Preparar FormData para multipart
        const randomScian = Math.floor(100000 + Math.random() * 900000)
        const randomClassification = 'SEC-' + Math.random().toString(36).substring(2, 7).toUpperCase()

        const formData = new FormData()
        formData.append('token', this.token)
        formData.append('password', this.form.password)
        formData.append('name', this.form.name)
        formData.append('address', this.form.address)
        formData.append('telephone', this.form.telephone)
        formData.append('email', this.form.company_email)
        formData.append('web_page', this.form.web_page || '')
        formData.append('scian', randomScian)
        formData.append('classification', randomClassification)
        formData.append('latitude', this.form.latitude || '')
        formData.append('longitude', this.form.longitude || '')
        formData.append('url_map', this.form.url_map || '')
        formData.append('validity', this.form.validity)
        if (this.logoFile) {
          formData.append('logo', this.logoFile)
        }

        const config = {
          method: 'post',
          url: `${API_BASE_URL}/security/create-company/`,
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          data: formData
        }

        axios(config)
          .then(resp => {
            this.loading = false
            this.$store.commit('snackbar_change_message', this.$t('security_company_created_success'))
            this.$store.commit('snackbar_change_color', 'success')
            this.$store.commit('snackbar_activate_true')
            // Redirigir al login después de 2 segundos
            setTimeout(() => {
              this.$router.push('/')
            }, 2000)
          })
          .catch(err => {
            this.loading = false
            this.error = (err.response && err.response.data && err.response.data.detail) || this.$t('error_creating_company')
          })
      }
    }
  },
  mounted() {
    this.token = this.$route.params.token
    if (!this.token) {
      this.error = this.$t('invalid_token')
      return
    }
    this.fetchInvitationData()
  }
}
</script>

<style scoped>
.mb-4 {
  margin-bottom: 30px;
}
.mb-3 {
  margin-bottom: 20px;
}
.mb-2 {
  margin-bottom: 16px;
}
.mt-4 {
  margin-top: 20px;
}
.my-4 {
  margin-top: 20px;
  margin-bottom: 20px;
}
.pa-4 {
  padding: 20px;
}
.pr-2 {
  padding-right: 8px;
}
.pl-2 {
  padding-left: 8px;
}
h3 {
  color: #333;
  font-weight: 500;
}
.ingla {
  border-radius: 2px;
  border-color: #002b48;
  border-width: 2px;
  border-style: solid;
}
.text-xs-center {
  text-align: center;
}
</style>
